import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Timer, Check, X, ArrowRight, Sparkles, Heart, ExternalLink, Star, Users, Clock, BarChart3 } from "lucide-react";

const YPTYeolPumTaKr = () => {
  const features = [
    "과목별 공부 시간 측정 및 상세 통계",
    "스터디 그룹 랭킹 및 경쟁 시스템",
    "캠스터디 기능으로 공부 사진 공유",
    "허용앱 설정으로 스마트폰 중독 방지",
    "월간/주간 공부 시간 분석 리포트",
    "비밀번호로 보호되는 프라이빗 스터디",
    "백그라운드 타이머 지속 기능",
    "서울대, 연세대 학생들이 애용하는 앱"
  ];

  const pros = [
    "경쟁을 통한 강력한 동기부여: 열품타의 스터디 그룹 시스템을 통해 같은 카테고리(수능, 의대 준비 등)의 사람들과 공부 시간을 비교할 수 있습니다. 다른 사람들이 열심히 공부하는 것을 보면 나도 더 열심히 하게 됩니다.",
    "캠스터디로 책임감 강화: 캠스터디 기능은 공부하는 동안 일정 간격으로 사진을 찍습니다. 이 사진들이 스터디 그룹과 공유되어 책임감을 부여합니다. 마치 다른 사람들과 함께 공부하는 느낌입니다.",
    "허용앱으로 집중력 향상: 공부 중에는 미리 승인한 앱만 사용할 수 있습니다. SNS나 게임의 유혹을 차단합니다. 허용되지 않은 앱을 열면 타이머가 일시정지됩니다.",
    "상세한 분석 데이터: 과목별 공부 시간 추적, 월간 트렌드 확인, 시간 분배 현황 파악이 가능합니다. 이 데이터로 공부 일정을 최적화하고 약점을 파악할 수 있습니다."
  ];

  const cons = [
    "모바일 전용 한계: 열품타는 iOS와 Android 앱으로만 제공됩니다. 웹 버전이 없어서 데스크톱이나 노트북에서는 사용할 수 없습니다.",
    "프리미엄 기능 유료화: 확장된 통계, 프리미엄 스터디 그룹 등 일부 고급 기능은 유료 구독이 필요합니다. 무료 버전은 그룹 참여에 제한이 있습니다.",
    "실시간 영상 상호작용 없음: CSW나 Focusmate와 달리 열품타는 실시간 영상 스터디 세션을 제공하지 않습니다. 캠스터디는 사진 공유 방식입니다."
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="열품타 (YPT-YeolPumTa) 리뷰 | 한국 대표 공부 타이머 앱 | CSW"
        description="열품타 리뷰: 330만 다운로드를 기록한 한국 대표 공부 앱. 스터디 그룹, 캠스터디, 동기부여 기능을 CSW와 비교해보세요."
        keywords="열품타, yeolpumta, 공부 타이머, 스터디 앱, 캠스터디, 공부 동기부여, 수능 공부, 한국 생산성 앱, 공부 시간 측정"
        canonicalUrl="/kr/online-study/ypt-yeolpumta/"
        hreflangTags={[
          { lang: "en", href: "/online-study/ypt-yeolpumta/" },
          { lang: "ko", href: "/kr/online-study/ypt-yeolpumta/" },
          { lang: "x-default", href: "/online-study/ypt-yeolpumta/" }
        ]}
      />
      
      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Review",
        "itemReviewed": {
          "@type": "SoftwareApplication",
          "name": "열품타 (YPT-YeolPumTa)",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "iOS, Android"
        },
        "author": {
          "@type": "Organization",
          "name": "CSW - Chill Study Work"
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "4.5",
          "bestRating": "5"
        },
        "reviewBody": "열품타는 한국 학생들 사이에서 가장 인기 있는 공부 타이머 앱입니다."
      })}} />
      
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium text-primary">
              <Timer className="w-4 h-4" />
              공부 타이머 & 동기부여
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in">
              <span className="text-gradient">열품타</span>
            </h1>
            
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-muted-foreground animate-fade-in" style={{ animationDelay: '0.1s' }}>
              YPT-YeolPumTa 리뷰 및 CSW 비교
            </h2>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              330만 다운로드를 기록한 한국 대표 공부 앱. 스터디 그룹, 캠스터디, 강력한 동기부여 기능으로 서울대·연세대 학생들이 애용합니다.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  CSW 무료로 시작하기 <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="font-semibold px-8 py-6 text-lg"
                asChild
              >
                <a href="https://yeolpumta.com/" target="_blank" rel="noopener noreferrer">
                  열품타 공식 사이트 <ExternalLink className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
            
            <div className="flex items-center justify-center gap-6 text-sm animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Heart className="w-4 h-4 text-primary fill-primary" />
                <span className="font-medium">CSW 완전 무료</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="font-medium">웹 기반 사용 가능</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                열품타 주요 <span className="text-gradient">기능</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                한국 학생들에게 사랑받는 열품타의 핵심 기능들
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { icon: <Users className="w-8 h-8" />, value: "330만+", label: "다운로드" },
              { icon: <Star className="w-8 h-8" />, value: "4.8", label: "앱스토어 평점" },
              { icon: <Clock className="w-8 h-8" />, value: "2017년", label: "출시 연도" },
              { icon: <BarChart3 className="w-8 h-8" />, value: "무료", label: "기본 사용료" },
            ].map((stat, index) => (
              <Card key={index} className="p-6 text-center bg-card/50 backdrop-blur-sm border-border/50">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-4 text-primary">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-foreground mb-1">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
            {/* Pros */}
            <div className="space-y-6">
              <h2 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-500" />
                </div>
                열품타 장점
              </h2>
              <div className="space-y-4">
                {pros.map((pro, index) => (
                  <Card key={index} className="p-6 bg-green-500/5 border-green-500/20">
                    <p className="text-muted-foreground leading-relaxed">{pro}</p>
                  </Card>
                ))}
              </div>
            </div>

            {/* Cons */}
            <div className="space-y-6">
              <h2 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <X className="w-5 h-5 text-red-500" />
                </div>
                열품타 단점
              </h2>
              <div className="space-y-4">
                {cons.map((con, index) => (
                  <Card key={index} className="p-6 bg-red-500/5 border-red-500/20">
                    <p className="text-muted-foreground leading-relaxed">{con}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Feedback */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <h2 className="font-display text-3xl font-bold text-foreground mb-6">
                사용자 <span className="text-gradient">후기</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                열품타는 한국 학생들 사이에서 문화적 현상이 되었습니다. 사용자들은 극적인 공부 습관 개선을 보고합니다: "스터디 그룹에서 뒤처지기 싫어서 하루 2시간에서 8시간으로 공부량이 늘었어요." 경쟁적 요소는 칭찬과 비판을 동시에 받습니다 - 어떤 이들은 동기부여가 된다고, 다른 이들은 스트레스를 받는다고 합니다. 서울대, 연세대 학생들이 특히 초기 사용자였고, 입소문을 통해 확산되었습니다. 유튜버 연고티비 등이 앱을 소개하며 인기가 급상승했습니다. 사용자들은 '페이스메이커' 효과를 높이 평가합니다 - 다른 사람들과 함께 공부하면 책임감이 생깁니다.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Best Use Cases */}
      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-3xl font-bold text-foreground mb-6 text-center">
              이런 분께 <span className="text-gradient">추천합니다</span>
            </h2>
            <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50">
              <p className="text-muted-foreground text-lg leading-relaxed">
                열품타는 고시험을 준비하는 한국 학생들에게 특히 적합합니다: 수능, 의대 입시, 공무원 시험, 자격증 준비 등. 경쟁 기반 동기부여를 원하고 한국어 앱 환경이 편한 분들에게 좋습니다. 스마트폰으로 공부 시간을 추적하고 싶은 모바일 우선 사용자에게 적합합니다. 캠스터디 기능은 주기적인 책임감 체크를 원하는 분들에게 맞습니다. <strong>웹 기반 공부, 데스크톱 접근, 또는 실시간 영상 코워킹을 선호한다면 CSW가 보완적인 기능을 제공합니다.</strong>
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CSW Comparison */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl font-bold mb-6">
              CSW는 웹에서 <span className="text-gradient">무료</span>로
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              열품타를 모바일에서 사용하면서, CSW로 데스크톱에서도 공부하세요. 실시간 스터디 스트림, 포모도로 타이머, 작업 관리까지 모두 무료입니다.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              {[
                { title: "실시간 스터디 스트림", desc: "전 세계 학생들과 함께 공부" },
                { title: "포모도로 타이머", desc: "집중과 휴식의 완벽한 균형" },
                { title: "작업 관리", desc: "칸반 보드로 할 일 정리" },
              ].map((item, index) => (
                <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-primary/20">
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </Card>
              ))}
            </div>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                CSW 무료로 시작하기 <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-display text-2xl font-bold text-foreground mb-6 text-center">
              관련 페이지
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/kr/" className="text-primary hover:underline">한국어 홈</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/online-study/focusmate/" className="text-primary hover:underline">Focusmate 리뷰</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/study-timer/" className="text-primary hover:underline">스터디 타이머</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/study-with-me/" className="text-primary hover:underline">Study With Me</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/online-study/ypt-yeolpumta/" className="text-primary hover:underline">English Version</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YPTYeolPumTaKr;
